import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useAdminMedia } from '../hooks/useAdminMedia'
import { useAdminMediaFolders } from '../hooks/useAdminMediaFolders'
import { MediaFolderTree } from '../components/media/MediaFolderTree'
import { MediaGrid } from '../components/media/MediaGrid'
import { MediaFolderCard } from '../components/media/MediaFolderCard'
import { MediaUploadZone } from '../components/media/MediaUploadZone'
import { MediaDetailPanel } from '../components/media/MediaDetailPanel'
import { MediaMoveDialog } from '../components/media/MediaMoveDialog'
import { MediaCropModal } from '../components/media/MediaCropModal'
import type { MediaDocument } from '../services/admin.media.service'
import type { MediaFolderDocument } from '../services/admin.mediaFolders.service'
import { useAuth } from '@/hooks/useAuth'

export function AdminMediaLibraryPage() {
  const { user } = useAuth()
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<MediaDocument | null>(null)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [movingFolder, setMovingFolder] = useState<MediaFolderDocument | null>(null)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { folders, createFolder, renameFolder, moveFolder, deleteFolder } = useAdminMediaFolders()

  const { items, loading, uploadProgress, uploadFiles, renameItem, moveItem, deleteItem, saveAsCopy, overwriteWithCrop } =
    useAdminMedia({ folderId: selectedFolderId })

  const filteredItems = search.trim()
    ? items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : items

  const subFolders = folders.filter((f) => f.parentId === selectedFolderId)

  // Keep selectedItem in sync with live data
  const liveSelectedItem = selectedItem
    ? (items.find((i) => i.id === selectedItem.id) ?? selectedItem)
    : null

  function handleSelectItem(item: MediaDocument) {
    setSelectedItem((prev) => (prev?.id === item.id ? null : item))
  }

  async function handleDeleteItem() {
    if (!liveSelectedItem) return
    await deleteItem(liveSelectedItem.id)
    setSelectedItem(null)
  }

  async function handleMoveItem(folderId: string | null) {
    if (!liveSelectedItem) return
    await moveItem(liveSelectedItem.id, folderId)
    setSelectedItem(null)
    setSelectedFolderId(folderId)
  }

  async function handleSaveAsCopy(blob: Blob) {
    if (!liveSelectedItem || !user) return
    await saveAsCopy(liveSelectedItem, blob)
  }

  async function handleOverwrite(blob: Blob) {
    if (!liveSelectedItem) return
    await overwriteWithCrop(liveSelectedItem.id, blob)
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Left sidebar: folder tree */}
      <aside className="flex w-52 shrink-0 flex-col border-r bg-background">
        <div className="border-b px-3 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Folders
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto px-1">
          <MediaFolderTree
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelect={setSelectedFolderId}
            onCreate={async (name, parentId) => { await createFolder(name, parentId) }}
            onRename={renameFolder}
            onMove={(folder) => setMovingFolder(folder)}
            onDelete={deleteFolder}
          />
        </div>
      </aside>

      {/* Center: upload zone + media grid */}
      <main className="flex flex-1 flex-col overflow-hidden">
        <div className="border-b px-4 py-3 flex items-center gap-3">
          <h1 className="text-sm font-semibold shrink-0">
            {selectedFolderId
              ? (folders.find((f) => f.id === selectedFolderId)?.name ?? 'Folder')
              : 'All Media'}
          </h1>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <MediaUploadZone
            onUpload={uploadFiles}
            uploadProgress={uploadProgress}
            disabled={selectedFolderId === null}
            disabledMessage="Select a folder to upload images"
          />

          {subFolders.length > 0 && !search.trim() && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Folders
              </p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
                {subFolders.map((folder) => (
                  <MediaFolderCard
                    key={folder.id}
                    folder={folder}
                    onClick={() => {
                      setSelectedItem(null)
                      setSelectedFolderId(folder.id)
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <MediaGrid
            items={filteredItems}
            loading={loading}
            selectedId={liveSelectedItem?.id ?? null}
            onSelect={handleSelectItem}
          />
        </div>
      </main>

      {/* Right panel: detail */}
      {liveSelectedItem && (
        <aside className="flex w-64 shrink-0 flex-col border-l bg-background">
          <div className="border-b px-3 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Details
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MediaDetailPanel
              item={liveSelectedItem}
              onRename={(name) => renameItem(liveSelectedItem.id, name)}
              onMove={() => setMoveDialogOpen(true)}
              onCrop={() => setCropModalOpen(true)}
              onDelete={handleDeleteItem}
            />
          </div>
        </aside>
      )}

      {/* Move dialog */}
      {liveSelectedItem && (
        <MediaMoveDialog
          open={moveDialogOpen}
          currentFolderId={liveSelectedItem.folderId}
          folders={folders}
          onClose={() => setMoveDialogOpen(false)}
          onMove={handleMoveItem}
        />
      )}

      {/* Folder move dialog */}
      {movingFolder && (() => {
        // Exclude the folder itself plus all its descendants as valid targets
        const excluded = new Set<string>([movingFolder.id])
        const stack = [movingFolder.id]
        while (stack.length) {
          const cur = stack.pop()!
          for (const f of folders) {
            if (f.parentId === cur && !excluded.has(f.id)) {
              excluded.add(f.id)
              stack.push(f.id)
            }
          }
        }
        return (
        <MediaMoveDialog
          open={!!movingFolder}
          currentFolderId={movingFolder.parentId}
          folders={folders}
          excludeIds={Array.from(excluded)}
          title={`Move "${movingFolder.name}"`}
          onClose={() => setMovingFolder(null)}
          onMove={async (parentId) => {
            try {
              await moveFolder(movingFolder.id, parentId)
              setMovingFolder(null)
            } catch (err) {
              alert(formatUserFacingError(err, 'Cannot move folder.'))
            }
          }}
        />
        )
      })()}

      {/* Crop modal */}
      {liveSelectedItem && liveSelectedItem.status === 'ready' && (
        <MediaCropModal
          open={cropModalOpen}
          item={liveSelectedItem}
          onClose={() => setCropModalOpen(false)}
          onSaveAsCopy={handleSaveAsCopy}
          onOverwrite={handleOverwrite}
        />
      )}
    </div>
  )
}
